<template>
  <span
    aria-hidden="true"
    class="text-body-1 text-static-brand-mid inline-flex"
  >
    <span
      ref="measurer"
      class="text-body-1 absolute invisible whitespace-nowrap"
    />
    <span
      v-for="(char, i) in display"
      :key="i"
      class="inline-block text-center"
      :style="{ width: slotWidth }"
      >{{ char }}</span
    >
  </span>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'

const {
  characters = ['₊', '⊹', '.', '°', '˖', '.'],
  speed = 120,
  length = 5,
} = defineProps<{
  characters?: string[]
  speed?: number
  length?: number
}>()

// Deterministic so it matches between server and client: the sequence must
// be visible in the SSR markup (before hydration/onMounted can run), and an
// SSR value randomized independently from the client's would mismatch on
// hydration.
const display = ref<string[]>(
  Array.from({ length }, (_, i) => characters[i % characters.length]),
)
const measurer = ref<HTMLSpanElement | null>(null)
const charWidth = ref(0)
let intervalId: ReturnType<typeof setInterval> | null = null

function randomChar() {
  return characters[Math.floor(Math.random() * characters.length)]
}

function initDisplay() {
  display.value = Array.from({ length }, randomChar)
}

function stream() {
  display.value = [...display.value.slice(1), randomChar()]
}

function measureWidest() {
  if (!measurer.value) return

  let max = 0
  for (const char of characters) {
    measurer.value.textContent = char
    max = Math.max(max, measurer.value.getBoundingClientRect().width)
  }
  charWidth.value = Math.ceil(max * 10) / 10
}

function stopInterval() {
  if (intervalId !== null) {
    clearInterval(intervalId)
    intervalId = null
  }
}

function startInterval() {
  stopInterval()
  initDisplay()
  measureWidest()

  // Respect the user's motion preference: leave the frozen initial
  // sequence on screen instead of cycling it.
  if (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)')?.matches
  ) {
    return
  }

  intervalId = setInterval(stream, speed)
}

const slotWidth = computed(() =>
  charWidth.value ? `${charWidth.value}px` : 'auto',
)

onMounted(startInterval)
onUnmounted(stopInterval)
</script>
